<template>
    <div v-if="pending">
        <div class="d-flex justify-content-center loading-center">
            <div class="spinner-border" role="status">
                <span class="sr-only">Loading...</span>
            </div>
        </div>
    </div>
	<div v-else class="container py-5">
        <h2>{{ $t('curveCreate') }}</h2>
        <nav aria-label="breadcrumb">
            <ol class="breadcrumb">
                  <li class="breadcrumb-item">
                    <RouterLink to="/modelings">
                        {{ $t('home') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">
                    <RouterLink :to="{ name: 'modeling', params: { id: this.$route.params.id } }">
                        {{ modelingName || $t('modelings') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">
                    <RouterLink :to="'/modeling/'+ this.$route.params.id + '/assettype/' + this.$route.params.assetTypeId" >
                        {{ assetTypeName || $t('assetType') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">{{ $t('create') }}</li>
            </ol>
        </nav>
        <div class="row mb-4">
            <div class="mb-3 col-md-4">
                <label class="form-label" for="name" required>{{ $t('name') }}</label>
                <input v-model="data.name" type="text" class="form-control" id="name" >
            </div>
            <div class="mb-3 col-md-8">
                <label class="form-label" for="description" required>{{ $t('description') }}</label>
                <input v-model="data.description" type="text" class="form-control" id="description" >
            </div>
        </div>
        <div class="row mb-4">
            <div class="mb-3 col-md-4">
                <label class="form-label" for="coefficientAttribute" required>{{ $t('coefficientAttribute') }}</label>
                <select id="coefficientAttribute" class="form-select" v-model="data.coefficientAttribute">
                    <option disabled>{{ $t('select') }}</option>
                    <template v-if="datasetHeaders.length > 0" >
                        <option v-for="(datasetHeader, i) in datasetHeaders" :key="i"  v-bind:value="datasetHeader.name">{{ datasetHeader.alias }}</option>
                    </template>
                </select> 
            </div>
            <div class="mb-3 col-md-4">
                <label class="form-label">&nbsp;</label>
                <div class="form-check">
                    <input class="form-check-input" type="checkbox" v-model="data.coefficientForecastOnly"  id="coefficientForecastOnly" checked>
                    <label class="form-check-label" for="coefficientForecastOnly">
                        {{ $t('coefficientForecastOnly') }}
                    </label>
                </div>
            </div>
        </div>
        <button class="fa fa-eye btn btn-outline-primary float-end" @click="showTableFunction"></button>
        <div class="mb-3" v-if="showTable">
            <h4>{{ $t('curveRange') }}</h4>
            <table class="table">
                <thead>
                    <tr>
                        <th scope="col"></th>
                        <th scope="col">{{ $t('startAge') }}</th>
                        <th scope="col">{{ $t('endAge') }}</th>
                        <th scope="col">{{ $t('startValue') }}</th>
                        <th scope="col">{{ $t('endValue') }}</th>
                    </tr>
                </thead> 
                <tbody>
                    <tr v-for="(curveRange, i) in data.curveRanges" class="align-middle"  :key="i">
                        <th scope="row"><i class="fa fa-remove" @click="removeCurveRange(i)"></i></th>
                        <td>
                            <input v-model="curveRange.startAge" disabled type="number" class="form-control" :id="'startAge_' + i">
                        </td>
                        <td>
                            <input v-model="curveRange.endAge" :max="data.lifespan" @input="updateCurveAgeStart(curveRange.endAge, i)" type="number" class="form-control" :id="'endAge_' + i">
                        </td>
                        <td>
                            <input v-model="curveRange.startValue" :disabled="i > 0" type="number" class="form-control" :id="'startValue_' + i">
                        </td>
                        <td>
                            <input v-model="curveRange.endValue" @input="updateCurveStartValue(curveRange.endValue, i)"  type="number" class="form-control" :id="'endValue_' + i">
                        </td>
                    </tr>
                </tbody>
            </table>
            <button class="btn btn-secondary w-25 mx-auto d-block" @click="addCurveRange"><i class="fa-solid fa-plus"></i></button>
        </div>
        <div v-else class="mb-3">
            <h4>{{ $t('curveRangeChart') }}</h4>
            <apexchart class="d-flex justify-content-center" type="line" width="800px" :options="chartOptions" :series="series"></apexchart>
        </div>
        <div class="mb-3">
            <h4 class="mb-3">{{ $t('curveFilters') }} </h4>
            <table class="table">
                <thead>
                    <tr>
                        <th scope="col"></th>
                        <th scope="col">{{ $t('name') }}</th>
                        <th scope="col">{{ $t('sign') }}</th>
                        <th scope="col">{{ $t('number') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(curveFilter, i) in data.curveFilters" class="align-middle"  :key="i" >
                        <th scope="row"><i class="fa fa-remove" @click="removeCurveFilter(i)"></i></th>
                        <td>
                            <select :id="'name_' + i" class="form-select" v-model="curveFilter.name">
                                <option disabled>{{ $t('select') }}</option>
                                <template v-if="datasetHeaders.length > 0" >
                                    <option v-for="(datasetHeader, i) in datasetHeaders" :key="i"  v-bind:value="datasetHeader.name">{{ datasetHeader.alias }}</option>
                                </template>
                            </select>
                        </td>
                        <td>
                            <select v-model="curveFilter.sign" class="form-select"  :id="'sign_' + i"> 
                                <option value="=">=</option>
                                <option value=">">&gt;</option>
                                <option value="<">&lt;</option>
                                <option value=">=">&gt;=</option>
                                <option value="<=">&lt;=</option>
                            </select>
                        </td>
                        <td>
                            <input v-model="curveFilter.number" type="number" class="form-control" :id="'number_' + i">
                        </td>
                    </tr>
                </tbody>
            </table>
            <button class="btn btn-secondary w-25 mx-auto d-block"  @click="addCurveFilter"><i class="fa-solid fa-plus"></i></button>
        </div>
        <div class="mb-3">
            <h4 class="mb-3">{{ $t('curveActivityEffects') }} </h4>
            <table class="table">
                <thead>
                    <tr>
                        <th scope="col"></th>
                        <th scope="col">{{ $t('name') }}</th>
                        <th scope="col">{{ $t('effect') }}</th>
                        <th scope="col">{{ $t('changeToCurve') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(curveActivityEffect, i) in data.curveActivityEffects" class="align-middle"  :key="i" >
                        <th scope="row"><i class="fa fa-remove" @click="removeCurveActivityEffect(i)"></i></th>
                        <td>
                            <select :id="'activityId_' + i" class="form-select" v-model="curveActivityEffect.activityId">
                                <option disabled>{{ $t('select') }}</option>
                                <template v-if="activityList.length > 0" >
                                    <option v-for="(activity, i) in activityList" :key="i"  v-bind:value="activity.id">{{ activity.name }}</option>
                                </template>
                            </select>
                        </td>
                        <td>
                            <div class="input-group">
                                <input v-model="curveActivityEffect.effect" type="number" class="form-control" :id="'effect_' + i">
                                <select :id="'effecttype_' + i" class="form-select" v-model="curveActivityEffect.effectType" >
                                    <option value="0">{{ $t('Absolute') }}</option>
                                    <option value="1">{{ $t('Relative') }}</option>
                                    <option value="2">{{ $t('Differential') }}</option>
                                </select>
                            </div>  
                        </td>
                        <td>
                            <select v-model="curveActivityEffect.changeToCurve" class="form-select"  :id="'changeToCurve_' + i"> 
                                <option disabled>{{ $t('select') }}</option>
                                <template v-if="curveList.length > 0" >
                                    <option v-for="(curve, i) in curveList" :key="i"  v-bind:value="curve.name">{{ curve.name }}</option>
                                </template>
                            </select>
                        </td>
                    </tr>
                </tbody>
            </table>
            <button class="btn btn-secondary w-25 mx-auto d-block" @click="addCurveActivityEffect"><i class="fa-solid fa-plus"></i></button>
        </div>
        <div class="d-grid gap-2 d-md-flex">
            <button type="button" class="btn btn-primary" @click="onSubmit">{{ $t('save') }}</button>
            <button type="button" class="btn btn-secondary" @click="$router.push({ path: `/modeling/${this.$route.params.id}/assettype/${this.$route.params.assetTypeId}` })">{{ $t('back') }}</button>
        </div>
    </div>
</template>

<script>
import AssetTypeApi from "../../api/server/assetType";
import DatasetApi from "../../api/server/dataset";
import CurveApi from "../../api/server/curve.js";
import ActivityApi from "../../api/server/activity.js";
import ModelingApi from "../../api/server/modeling.js";

export default {
    name: "curvecreate",
    watch: {
        $route: "requestData",
    },
    mounted() {
        this.requestData();
    },
    methods: {
        async requestData() {
            this.pending = true;

            this.data.parameterId = this.$route.params.parameterId;
            var assetType = await AssetTypeApi.assetTypeDetail(this.$route.params.id, this.$route.params.assetTypeId);
            this.assetTypeName = assetType.name;

            var dataset = await DatasetApi.datasetDetail(this.$route.params.id, assetType.datasetId);

            var activityList = await ActivityApi.activityList(this.$route.params.id);
            this.activityList = activityList.filter(a => a.assetTypeId == this.$route.params.assetTypeId);

            var datasetContent = JSON.parse(dataset.content);
            this.datasetHeaders = datasetContent["headers"];

            var curveList = await CurveApi.curveList(this.$route.params.id);
            this.curveList = curveList.filter(a => a.parameterId == this.$route.params.parameterId);
        
            var modeling = await ModelingApi.modelingDetail(this.$route.params.id);
            this.modelingName = modeling.name;

            this.pending = false;
        },
        async onSubmit() {
            try {
                if(this.data.name.length > 0) {
                    this.data.curveGroup = [{
                        id: 0,
                        name: "All Asset Types",
                        curveGroupAssetType: []
                    }]

                    await CurveApi.curveCreate(this.$route.params.id, this.data);
                    this.showSuccess();
                    this.$router.go(-1);
                } else {
                    this.showEmptyNameError();
                }
            } catch {
                this.showError();
            }
        },
        showTableFunction() {
            this.generateGraph();
            this.showTable = !this.showTable;
        },
        generateGraph() {
            let seriesData = [];

            for (let range of this.data.curveRanges) {
                seriesData.push({ x: range.startAge, y: range.startValue });
                seriesData.push({ x: range.endAge, y: range.endValue });
            }
            
            this.series = [{
                name: "Value",
                data: seriesData
            }];
        },
        removeCurveFilter(rowIndex) {
            this.data.curveFilters.splice(rowIndex, 1);
        },
        addCurveFilter() {
            this.data.curveFilters.push({  
                name: "",
                sign: "=",
                number: 0
            })
        },
        removeCurveActivityEffect(rowIndex) {
            this.data.curveActivityEffects.splice(rowIndex, 1);
        },
        addCurveActivityEffect() {
            this.data.curveActivityEffects.push({
                effect: 0,
                effectType: 0,
                changeToCurveId: 0
            })
        },
        updateCurveAgeStart(value, i) {
            var range = this.data.curveRanges[i+1];
            if(range) {
                range.startAge = value;
            }
        },
        updateCurveStartValue(value, i) {
            var range = this.data.curveRanges[i+1];
            if(range) {
                range.startValue = value;
            }
        },
        removeCurveRange(rowIndex) {
            this.data.curveRanges.splice(rowIndex, 1);
        },
        addCurveRange() {
            this.data.curveRanges.push({  
                description: "",
                startAge: this.getMinStartAge(),
                endAge: 0,
                startValue: this.getMinStartValue(),
                endValue: 0.0
            })
        },
        getMinStartAge() {
            var minStartAge = 0;
            if(this.data.curveRanges.length > 0)
            minStartAge = this.data.curveRanges[this.data.curveRanges.length -1].endAge;

            return minStartAge;
        },
        getMinStartValue() {
            var minStartValue = 0;
            if(this.data.curveRanges.length > 0)
            minStartValue = this.data.curveRanges[this.data.curveRanges.length -1].endValue;

            return minStartValue;
        },
    },
    notifications: {
        showSuccess: {
            title: 'success',
            message: 'curveCreated',
            type: 'success'
        },
        showError: {
            title: 'error',
            message: 'curveError',
            type: 'error'
        },
        showEmptyNameError: { 
            title: 'error',
            message: 'emptyName',
            type: 'error'
        }
    },
    data() {
        return {
            showTable: true,
            data: {
                id: 0,
                name: "",
                description: "",
                curveRanges: [],
                curveFilters: [],
                curveActivityEffects: [],
                coefficientAttribute: "",
                coefficientForecastOnly: false
            },
            curveList: [],
            pending: true,
            modelingName: "",
            series: [],
            chartOptions: {
                chart: {
                    type: 'line',
                    animations: {
                        enabled: false
                    },
                    height: 350,
                    toolbar: {
                        show: false
                    }
                },
                xaxis: {
                    title: {
                        text: 'Age',
                    }
                },
                yaxis: {
                    title: {
                        text: 'Value',
                    },
                },
            }
        }
    }
};
</script>